import { TreeResponse, TreeRelationship } from '@/types/tree';
import { type HybridPedigreeStoreType } from '@/stores/HybridPedigreeStore';
import { familyStore } from '@/stores/FamilyStore';
import type { RelationshipType } from 'we-tree-api-client/v2';

export interface LinePoint {
  x: number;
  y: number;
}

export interface RelationshipLine {
  id: string;
  fromFamilyMemberId: string;
  toFamilyMemberId: string;
  fromPoint: LinePoint;
  toPoint: LinePoint;
  type: 'parent-child' | 'spouse';
  isDashed?: boolean;
  /** When set, render as a rounded polyline instead of a single segment. */
  points?: LinePoint[];
}

interface ParentGroup {
  parentIds: string[];
  children: string[];
  parentCenterPoint: LinePoint;
}

export interface LineCalculationOptions {
  containerPadding: number;
  cardWidth: number;
  cardHeight: number;
  lineMargin: number;
  /** Vertical offset for horizontal connector lines (spouse/branch); reduced for short viewports */
  connectorLineExtension: number;
}

/** Default options for line calculation; exported for merging in UI (e.g. short viewport overrides). */
export const DEFAULT_LINE_OPTIONS: LineCalculationOptions = {
  containerPadding: 20,
  cardWidth: 220,
  /* Match hybrid-pedigree-screen.css v0.1 tree card min-height */
  cardHeight: 130,
  lineMargin: 10,
  connectorLineExtension: 15
};

const DEFAULT_OPTIONS = DEFAULT_LINE_OPTIONS;

/** Viewport height (px) below which we use tighter line spacing (wide short screens). */
export const SHORT_VIEWPORT_HEIGHT_PX = 850;
/** Very short viewport: minimize vertical line length so chart fits with less scroll. */
export const VERY_SHORT_VIEWPORT_HEIGHT_PX = 550;
/** Viewport width (px) below which grandparents are hidden (phone); use shorter lines in this compressed layout. */
export const PHONE_VIEWPORT_WIDTH_PX = 480;
/** Landscape phone/tablet: short height with width > height (e.g. iPhone ~874×402). */
export const COMPACT_LANDSCAPE_MAX_HEIGHT_PX = 500;

/** True when the tree uses the compressed layout (phone portrait or phone landscape). */
export function isCompactTreeViewport(windowHeightPx: number, windowWidthPx?: number): boolean {
  if (windowWidthPx === undefined) return false;
  if (windowWidthPx <= PHONE_VIEWPORT_WIDTH_PX) return true;
  return windowHeightPx <= COMPACT_LANDSCAPE_MAX_HEIGHT_PX && windowWidthPx > windowHeightPx;
}

/**
 * Line options for the current viewport (single source of truth for line spacing).
 * Uses both height and width: phone width (grandparents hidden) gets short lines; short height gets short/very-short lines.
 * Used by RelationshipLines and by layout CSS variable calculation.
 */
export function getLineOptionsForViewport(
  windowHeightPx: number,
  windowWidthPx?: number,
  customOptions?: Partial<LineCalculationOptions>
): LineCalculationOptions {
  const base = { ...DEFAULT_LINE_OPTIONS, ...customOptions };
  const isCompact = isCompactTreeViewport(windowHeightPx, windowWidthPx);
  const isShortHeight = windowHeightPx <= SHORT_VIEWPORT_HEIGHT_PX;
  const isVeryShortHeight = windowHeightPx <= VERY_SHORT_VIEWPORT_HEIGHT_PX;

  if (!isShortHeight && !isCompact) return base;
  if (isVeryShortHeight || isCompact) {
    return {
      ...base,
      cardWidth: 160,
      cardHeight: 118,
      lineMargin: 1,
      connectorLineExtension: 2
    };
  }
  return { ...base, lineMargin: 2, connectorLineExtension: 3 };
}

/**
 * Minimum vertical space (px) required between two generation rows so relationship
 * lines fit without overlapping cards. Derived from line options.
 */
export function getMinVerticalLineSpacePx(options: LineCalculationOptions): number {
  return 2 * (options.lineMargin + options.connectorLineExtension);
}

/** Default extension when not overridden by options (back-compat) */
const DEFAULT_CONNECTOR_EXTENSION = 15;

/** Corner radius for connector polylines (px). */
export const STEPPED_LINE_CORNER_RADIUS = 10;

/** Minimum straight run before/after a bend so arcs can use the full corner radius. */
const CORNER_ARM = STEPPED_LINE_CORNER_RADIUS * 2;

function mergeGapIntervals(intervals: Array<[number, number]>): Array<[number, number]> {
  if (intervals.length === 0) {
    return [];
  }
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const [gapStart, gapEnd] = sorted[i];
    if (gapStart <= last[1]) {
      last[1] = Math.max(last[1], gapEnd);
    } else {
      merged.push([gapStart, gapEnd]);
    }
  }
  return merged;
}

/** Draw horizontal segments on branchY, leaving gaps around rounded vertical junctions. */
function pushHorizontalSegments(
  lines: RelationshipLine[],
  idPrefix: string,
  startX: number,
  endX: number,
  branchY: number,
  gapCentersX: number[],
  fromFamilyMemberId: string,
  toFamilyMemberId: string,
  isDashed = false
): void {
  if (endX - startX <= 1) {
    return;
  }

  const gaps = mergeGapIntervals(
    gapCentersX
      .map((centerX): [number, number] => [centerX - CORNER_ARM, centerX + CORNER_ARM])
      .filter(([gapStart, gapEnd]) => gapEnd > startX && gapStart < endX)
  );

  let cursor = startX;
  let segmentIndex = 0;
  for (const [gapStart, gapEnd] of gaps) {
    if (cursor < gapStart) {
      lines.push({
        id: `${idPrefix}-${segmentIndex}`,
        fromFamilyMemberId,
        toFamilyMemberId,
        fromPoint: { x: cursor, y: branchY },
        toPoint: { x: gapStart, y: branchY },
        type: 'parent-child',
        isDashed
      });
      segmentIndex += 1;
    }
    cursor = Math.max(cursor, gapEnd);
  }

  if (cursor < endX) {
    lines.push({
      id: `${idPrefix}-${segmentIndex}`,
      fromFamilyMemberId,
      toFamilyMemberId,
      fromPoint: { x: cursor, y: branchY },
      toPoint: { x: endX, y: branchY },
      type: 'parent-child',
      isDashed
    });
  }
}

/**
 * Look up relationship type from FamilyStore
 * Checks both directions (familyMember1Id->familyMember2Id and familyMember2Id->familyMember1Id)
 * Returns the actual relationship type, or 'biological' as fallback if not found
 * @param context Optional context string for debugging (e.g., "parents-to-root", "root-to-children")
 */
function getRelationshipType(
  fromFamilyMemberId: string,
  toFamilyMemberId: string,
  expectedType: 'spousal' | 'parent-child' = 'parent-child',
  _context?: string
): RelationshipType {
  if (!familyStore.isLoaded) {
    return expectedType === 'spousal' ? 'spousal' : 'biological';
  }

  // Look up relationship in FamilyStore
  // For parent-child: familyMember1Id = parent, familyMember2Id = child (directional)
  // For spousal: order doesn't matter (bidirectional)
  for (const rel of familyStore.relationships.values()) {
    const isMatch =
      (rel.familyMember1Id === fromFamilyMemberId && rel.familyMember2Id === toFamilyMemberId) ||
      (rel.familyMember2Id === fromFamilyMemberId && rel.familyMember1Id === toFamilyMemberId);

    if (isMatch) {
      // For spousal relationships, return spousal if it's a spousal relationship
      if (expectedType === 'spousal' && rel.relationshipType === 'spousal') {
        return 'spousal';
      }

      // For parent-child relationships, return the actual relationship type
      // Note: parent-child relationships are stored as parent (familyMember1) -> child (familyMember2)
      // The relationship is found regardless of direction, so we can return it
      if (expectedType === 'parent-child' && rel.isParentChild) {
        return rel.relationshipType;
      }
    }
  }

  // Fallback to default types if relationship not found
  // This can happen if:
  // 1. Relationships haven't been loaded yet
  // 2. Placeholder relationships (which don't exist in FamilyStore)
  // 3. Relationships between people not in the current family
  // 4. The relationship exists but in the wrong direction for what we're looking for
  return expectedType === 'spousal' ? 'spousal' : 'biological';
}

/**
 * Extract relationships from HybridPedigreeStore structure
 */
function extractRelationshipsFromStore(store: HybridPedigreeStoreType): TreeRelationship[] {
  const relationships: TreeRelationship[] = [];

  // Extract relationships from the store structure
  // Look up actual relationship types from FamilyStore

  // Determine which side the TreeAnchor is on (left or right)
  const treeAnchorFamilyMemberId = store.treeAnchorFamilyMemberId;
  const isTreeAnchorLeft = treeAnchorFamilyMemberId === store.rootGen.leftRootFamilyMemberId;
  const isTreeAnchorRight = treeAnchorFamilyMemberId === store.rootGen.rightRootFamilyMemberId;

  // Get the TreeAnchor's root position (the side the TreeAnchor is on)
  const treeAnchorRootFamilyMemberId = isTreeAnchorLeft
    ? store.rootGen.leftRootFamilyMemberId
    : isTreeAnchorRight
      ? store.rootGen.rightRootFamilyMemberId
      : null;

  // Root generation spouse relationship
  if (store.rootGen.leftRootFamilyMemberId && store.rootGen.rightRootFamilyMemberId) {
    const spouseType = getRelationshipType(
      store.rootGen.leftRootFamilyMemberId,
      store.rootGen.rightRootFamilyMemberId,
      'spousal'
    );
    relationships.push({
      id: `spouse-${store.rootGen.leftRootFamilyMemberId}-${store.rootGen.rightRootFamilyMemberId}`,
      fromFamilyMemberId: store.rootGen.leftRootFamilyMemberId,
      toFamilyMemberId: store.rootGen.rightRootFamilyMemberId,
      type: spouseType
    });
  }

  // Parents generation relationships
  if (store.parentsGen.leftParentFamilyMemberId && store.parentsGen.rightParentFamilyMemberId) {
    const spouseType = getRelationshipType(
      store.parentsGen.leftParentFamilyMemberId,
      store.parentsGen.rightParentFamilyMemberId,
      'spousal'
    );
    relationships.push({
      id: `spouse-${store.parentsGen.leftParentFamilyMemberId}-${store.parentsGen.rightParentFamilyMemberId}`,
      fromFamilyMemberId: store.parentsGen.leftParentFamilyMemberId,
      toFamilyMemberId: store.parentsGen.rightParentFamilyMemberId,
      type: spouseType
    });
  }

  // Parent-child relationships from parents to root
  // Only create relationships to the TreeAnchor's side (not the spouse's side)
  if (treeAnchorRootFamilyMemberId) {
    if (store.parentsGen.leftParentFamilyMemberId) {
      const relationshipType = getRelationshipType(
        store.parentsGen.leftParentFamilyMemberId,
        treeAnchorRootFamilyMemberId,
        'parent-child',
        'parents-to-root'
      );
      relationships.push({
        id: `parent-${store.parentsGen.leftParentFamilyMemberId}-${treeAnchorRootFamilyMemberId}`,
        fromFamilyMemberId: store.parentsGen.leftParentFamilyMemberId,
        toFamilyMemberId: treeAnchorRootFamilyMemberId,
        type: relationshipType
      });
    }

    if (store.parentsGen.rightParentFamilyMemberId) {
      const relationshipType = getRelationshipType(
        store.parentsGen.rightParentFamilyMemberId,
        treeAnchorRootFamilyMemberId,
        'parent-child',
        'parents-to-root'
      );
      relationships.push({
        id: `parent-${store.parentsGen.rightParentFamilyMemberId}-${treeAnchorRootFamilyMemberId}`,
        fromFamilyMemberId: store.parentsGen.rightParentFamilyMemberId,
        toFamilyMemberId: treeAnchorRootFamilyMemberId,
        type: relationshipType
      });
    }
  }

  // Children relationships from root to children
  // Need to check relationships from BOTH left and right root persons to children
  // because children can belong to either spouse (important for step-relationships)
  store.childrenGen.childrenFamilyMemberIds.forEach((childFamilyMemberId) => {
    // Check relationship from left root person to child
    if (store.rootGen.leftRootFamilyMemberId) {
      const relationshipType = getRelationshipType(
        store.rootGen.leftRootFamilyMemberId,
        childFamilyMemberId,
        'parent-child',
        'root-to-children'
      );
      relationships.push({
        id: `parent-${store.rootGen.leftRootFamilyMemberId}-${childFamilyMemberId}`,
        fromFamilyMemberId: store.rootGen.leftRootFamilyMemberId,
        toFamilyMemberId: childFamilyMemberId,
        type: relationshipType
      });
    }

    // Check relationship from right root person to child
    if (store.rootGen.rightRootFamilyMemberId) {
      const relationshipType = getRelationshipType(
        store.rootGen.rightRootFamilyMemberId,
        childFamilyMemberId,
        'parent-child',
        'root-to-children'
      );
      relationships.push({
        id: `parent-${store.rootGen.rightRootFamilyMemberId}-${childFamilyMemberId}`,
        fromFamilyMemberId: store.rootGen.rightRootFamilyMemberId,
        toFamilyMemberId: childFamilyMemberId,
        type: relationshipType
      });
    }
  });

  // Grandparents relationships - left side
  if (
    store.grandparentsGen.leftGrandparentsLeftFamilyMemberId &&
    store.grandparentsGen.leftGrandparentsRightFamilyMemberId
  ) {
    // Spouse relationship between left grandparents
    const spouseType = getRelationshipType(
      store.grandparentsGen.leftGrandparentsLeftFamilyMemberId,
      store.grandparentsGen.leftGrandparentsRightFamilyMemberId,
      'spousal'
    );
    relationships.push({
      id: `spouse-${store.grandparentsGen.leftGrandparentsLeftFamilyMemberId}-${store.grandparentsGen.leftGrandparentsRightFamilyMemberId}`,
      fromFamilyMemberId: store.grandparentsGen.leftGrandparentsLeftFamilyMemberId,
      toFamilyMemberId: store.grandparentsGen.leftGrandparentsRightFamilyMemberId,
      type: spouseType
    });

    // Parent-child relationships from left grandparents to left parent
    if (store.parentsGen.leftParentFamilyMemberId) {
      const leftGrandparentType = getRelationshipType(
        store.grandparentsGen.leftGrandparentsLeftFamilyMemberId,
        store.parentsGen.leftParentFamilyMemberId,
        'parent-child',
        'grandparents-to-parents'
      );
      const rightGrandparentType = getRelationshipType(
        store.grandparentsGen.leftGrandparentsRightFamilyMemberId,
        store.parentsGen.leftParentFamilyMemberId,
        'parent-child',
        'grandparents-to-parents'
      );
      relationships.push({
        id: `parent-${store.grandparentsGen.leftGrandparentsLeftFamilyMemberId}-${store.parentsGen.leftParentFamilyMemberId}`,
        fromFamilyMemberId: store.grandparentsGen.leftGrandparentsLeftFamilyMemberId,
        toFamilyMemberId: store.parentsGen.leftParentFamilyMemberId,
        type: leftGrandparentType
      });
      relationships.push({
        id: `parent-${store.grandparentsGen.leftGrandparentsRightFamilyMemberId}-${store.parentsGen.leftParentFamilyMemberId}`,
        fromFamilyMemberId: store.grandparentsGen.leftGrandparentsRightFamilyMemberId,
        toFamilyMemberId: store.parentsGen.leftParentFamilyMemberId,
        type: rightGrandparentType
      });
    }
  }

  // Grandparents relationships - right side
  if (
    store.grandparentsGen.rightGrandparentsLeftFamilyMemberId &&
    store.grandparentsGen.rightGrandparentsRightFamilyMemberId
  ) {
    // Spouse relationship between right grandparents
    const spouseType = getRelationshipType(
      store.grandparentsGen.rightGrandparentsLeftFamilyMemberId,
      store.grandparentsGen.rightGrandparentsRightFamilyMemberId,
      'spousal'
    );
    relationships.push({
      id: `spouse-${store.grandparentsGen.rightGrandparentsLeftFamilyMemberId}-${store.grandparentsGen.rightGrandparentsRightFamilyMemberId}`,
      fromFamilyMemberId: store.grandparentsGen.rightGrandparentsLeftFamilyMemberId,
      toFamilyMemberId: store.grandparentsGen.rightGrandparentsRightFamilyMemberId,
      type: spouseType
    });

    // Parent-child relationships from right grandparents to right parent
    if (store.parentsGen.rightParentFamilyMemberId) {
      const leftGrandparentType = getRelationshipType(
        store.grandparentsGen.rightGrandparentsLeftFamilyMemberId,
        store.parentsGen.rightParentFamilyMemberId,
        'parent-child',
        'grandparents-to-parents'
      );
      const rightGrandparentType = getRelationshipType(
        store.grandparentsGen.rightGrandparentsRightFamilyMemberId,
        store.parentsGen.rightParentFamilyMemberId,
        'parent-child',
        'grandparents-to-parents'
      );
      relationships.push({
        id: `parent-${store.grandparentsGen.rightGrandparentsLeftFamilyMemberId}-${store.parentsGen.rightParentFamilyMemberId}`,
        fromFamilyMemberId: store.grandparentsGen.rightGrandparentsLeftFamilyMemberId,
        toFamilyMemberId: store.parentsGen.rightParentFamilyMemberId,
        type: leftGrandparentType
      });
      relationships.push({
        id: `parent-${store.grandparentsGen.rightGrandparentsRightFamilyMemberId}-${store.parentsGen.rightParentFamilyMemberId}`,
        fromFamilyMemberId: store.grandparentsGen.rightGrandparentsRightFamilyMemberId,
        toFamilyMemberId: store.parentsGen.rightParentFamilyMemberId,
        type: rightGrandparentType
      });
    }
  }

  return relationships;
}

/**
 * Determine if a relationship should be rendered with a dashed line
 * For parent-child relationships:
 * - Biological relationships: solid line (return false)
 * - Non-biological relationships (adoptive, foster, step): dashed line (return true)
 * For spouse relationships: solid line (return false)
 */
function shouldUseDashedLine(
  fromFamilyMemberId: string,
  toFamilyMemberId: string,
  relationships: TreeRelationship[],
  lineType: 'spouse' | 'parent-child' = 'parent-child'
): boolean {
  // Find the relationship between these two people
  const relationship = relationships.find(
    (rel) =>
      (rel.fromFamilyMemberId === fromFamilyMemberId && rel.toFamilyMemberId === toFamilyMemberId) ||
      (rel.fromFamilyMemberId === toFamilyMemberId && rel.toFamilyMemberId === fromFamilyMemberId)
  );

  if (!relationship) {
    // No relationship found - default to solid line
    // This can happen for placeholder relationships or when relationships aren't loaded yet
    return false;
  }

  // For spouse relationships, always use solid lines (never dashed)
  if (lineType === 'spouse') {
    return false;
  }

  // For parent-child relationships, dash all non-biological relationships
  // (adoptive, foster, step should be dashed; biological should be solid)
  if (lineType === 'parent-child') {
    // Only dash non-biological parent-child relationships
    // Never dash spousal relationships (they should never get here, but safety check)
    if (relationship.type === 'spousal') {
      return false;
    }

    const shouldDash = relationship.type !== 'biological';

    return shouldDash;
  }

  // Default: solid line
  return false;
}

/**
 * Calculate the center point of a person card
 */
function getCardCenterPoint(
  familyMemberId: string,
  containerRect: DOMRect,
  _options: LineCalculationOptions = DEFAULT_OPTIONS
): LinePoint | null {
  const cardElement = document.querySelector(`[data-family-member-id="${familyMemberId}"]`);
  if (!cardElement) return null;

  const cardRect = cardElement.getBoundingClientRect();

  // Skip cards that are hidden (display: none returns zero dimensions)
  if (cardRect.width === 0 || cardRect.height === 0) return null;

  // Calculate relative to container using actual card dimensions (works for responsive sizes)
  const relativeX = cardRect.left - containerRect.left + cardRect.width / 2;
  const relativeY = cardRect.top - containerRect.top + cardRect.height / 2;

  return { x: relativeX, y: relativeY };
}

/**
 * Calculate the top center point of a person card (for connections to descendants below)
 */
function getCardTopCenterPoint(
  familyMemberId: string,
  containerRect: DOMRect,
  _options: LineCalculationOptions = DEFAULT_OPTIONS
): LinePoint | null {
  const cardElement = document.querySelector(`[data-family-member-id="${familyMemberId}"]`);
  if (!cardElement) return null;

  const cardRect = cardElement.getBoundingClientRect();

  // Skip cards that are hidden (display: none returns zero dimensions)
  if (cardRect.width === 0 || cardRect.height === 0) return null;

  // Calculate relative to container using actual card dimensions (works for responsive sizes)
  const relativeX = cardRect.left - containerRect.left + cardRect.width / 2;
  const relativeY = cardRect.top - containerRect.top;

  return { x: relativeX, y: relativeY };
}

/**
 * Calculate the bottom center point of a person card (for connections to ancestors above)
 */
function getCardBottomCenterPoint(
  familyMemberId: string,
  containerRect: DOMRect,
  _options: LineCalculationOptions = DEFAULT_OPTIONS
): LinePoint | null {
  const cardElement = document.querySelector(`[data-family-member-id="${familyMemberId}"]`);
  if (!cardElement) return null;

  const cardRect = cardElement.getBoundingClientRect();

  // Skip cards that are hidden (display: none returns zero dimensions)
  if (cardRect.width === 0 || cardRect.height === 0) return null;

  // Calculate relative to container using actual card dimensions (works for responsive sizes)
  const relativeX = cardRect.left - containerRect.left + cardRect.width / 2;
  const relativeY = cardRect.top - containerRect.top + cardRect.height;

  return { x: relativeX, y: relativeY };
}

/**
 * Group parent-child relationships by parent pairs
 */
function groupParentChildRelationships(
  treeData: TreeResponse | HybridPedigreeStoreType,
  containerRect: DOMRect,
  options: LineCalculationOptions = DEFAULT_OPTIONS
): ParentGroup[] {
  // Extract relationships based on data type
  const relationships = 'relationships' in treeData ? treeData.relationships : extractRelationshipsFromStore(treeData);

  const parentChildRelationships = relationships.filter(
    (rel: TreeRelationship) =>
      rel.type === 'biological' || rel.type === 'adoptive' || rel.type === 'foster' || rel.type === 'step'
  );

  // Group by parent pair (spouse relationships)
  const spouseRelationships = relationships.filter((rel: TreeRelationship) => rel.type === 'spousal');

  const spouseGroups = new Map<string, string[]>();

  spouseRelationships.forEach((rel: TreeRelationship) => {
    const key = [rel.fromFamilyMemberId, rel.toFamilyMemberId].sort().join('-');
    if (!spouseGroups.has(key)) {
      spouseGroups.set(key, [rel.fromFamilyMemberId, rel.toFamilyMemberId]);
    }
  });

  // Group parent-child relationships by parent pair
  const parentGroups = new Map<string, ParentGroup>();

  parentChildRelationships.forEach((rel: TreeRelationship) => {
    const parentFamilyMemberId = rel.fromFamilyMemberId;
    const childFamilyMemberId = rel.toFamilyMemberId;

    // Find which spouse group this parent belongs to
    let groupKey = '';
    for (const [key, spousePair] of spouseGroups) {
      if (spousePair.includes(parentFamilyMemberId)) {
        groupKey = key;
        break;
      }
    }

    // If no spouse group found, create individual group
    if (!groupKey) {
      groupKey = `single-${parentFamilyMemberId}`;
    }

    if (!parentGroups.has(groupKey)) {
      parentGroups.set(groupKey, {
        parentIds: groupKey.startsWith('single-')
          ? [parentFamilyMemberId]
          : spouseGroups.get(groupKey) || [parentFamilyMemberId],
        children: [],
        parentCenterPoint: { x: 0, y: 0 }
      });
    }

    // Only add child if not already in the list (avoid duplicates from multiple parents)
    const group = parentGroups.get(groupKey)!;
    if (!group.children.includes(childFamilyMemberId)) {
      group.children.push(childFamilyMemberId);
    }
  });

  // Calculate center points for each parent group
  const parentGroupsArray: ParentGroup[] = [];
  for (const [, group] of parentGroups) {
    const parentPoints = group.parentIds
      .map((parentFamilyMemberId) => getCardCenterPoint(parentFamilyMemberId, containerRect, options))
      .filter((point): point is LinePoint => point !== null);

    if (parentPoints.length > 0) {
      // Calculate center point between all parents in the group
      const centerX = parentPoints.reduce((sum, point) => sum + point.x, 0) / parentPoints.length;
      const centerY = Math.max(...parentPoints.map((point) => point.y));

      group.parentCenterPoint = { x: centerX, y: centerY };
      parentGroupsArray.push(group);
    }
  }
  return parentGroupsArray;
}

/**
 * Calculate all relationship lines for the pedigree chart with centralized branching
 */
export function calculateRelationshipLines(
  treeData: TreeResponse | HybridPedigreeStoreType,
  containerElement: HTMLElement,
  options: LineCalculationOptions = DEFAULT_OPTIONS
): RelationshipLine[] {
  const lines: RelationshipLine[] = [];
  const containerRect = containerElement.getBoundingClientRect();

  // Extract relationships based on data type
  const relationships = 'relationships' in treeData ? treeData.relationships : extractRelationshipsFromStore(treeData);

  // Get grouped parent-child relationships
  const parentGroups = groupParentChildRelationships(treeData, containerRect, options);

  // First, create spouse lines for all spouse pairs (even those without children)
  parentGroups.forEach((group) => {
    // Create spouse lines for any group with multiple parents
    if (group.parentIds.length > 1) {
      const parentBottomPoints = group.parentIds
        .map((parentId) => getCardBottomCenterPoint(parentId, containerRect, options))
        .filter((point): point is LinePoint => point !== null);

      if (parentBottomPoints.length >= 2) {
        const leftParent = parentBottomPoints[0];
        const rightParent = parentBottomPoints[parentBottomPoints.length - 1];

        // Create horizontal spouse line (positioned below the parent cards)
        // Position it further from the card edge for better visibility
        const connectorExt = options.connectorLineExtension ?? DEFAULT_CONNECTOR_EXTENSION;
        const horizontalLineY = leftParent.y + options.lineMargin + connectorExt;
        const isSpouseDashed = shouldUseDashedLine(group.parentIds[0], group.parentIds[1], relationships, 'spouse');
        const spouseLineStartX = leftParent.x + CORNER_ARM;
        const spouseLineEndX = rightParent.x - CORNER_ARM;
        if (spouseLineStartX < spouseLineEndX) {
          lines.push({
            id: `parent-horizontal-${group.parentIds.join('-')}`,
            fromFamilyMemberId: group.parentIds[0],
            toFamilyMemberId: group.parentIds[1],
            fromPoint: { x: spouseLineStartX, y: horizontalLineY },
            toPoint: { x: spouseLineEndX, y: horizontalLineY },
            type: 'spouse',
            isDashed: isSpouseDashed
          });
        }

        // Create short vertical lines from each parent down to the horizontal line
        // Dashed when that parent has a non-biological relationship with any child in the group
        parentBottomPoints.forEach((parentPoint, index) => {
          const parentId = group.parentIds[index];
          const hasNonBioWithAnyChild =
            group.children.length > 0 &&
            group.children.some((childId) => {
              const rel = relationships.find(
                (r) =>
                  (r.fromFamilyMemberId === parentId && r.toFamilyMemberId === childId) ||
                  (r.fromFamilyMemberId === childId && r.toFamilyMemberId === parentId)
              );
              return rel && rel.type !== 'biological' && rel.type !== 'spousal';
            });
          const towardCenterX = index === 0 ? 1 : -1;
          const bendPoint = {
            x: parentPoint.x + towardCenterX * CORNER_ARM,
            y: horizontalLineY
          };
          lines.push({
            id: `parent-to-horizontal-${group.parentIds[index]}-${group.parentIds.join('-')}`,
            fromFamilyMemberId: group.parentIds[index],
            toFamilyMemberId: group.parentIds[0],
            fromPoint: { x: parentPoint.x, y: parentPoint.y },
            toPoint: { x: parentPoint.x, y: horizontalLineY },
            points: [{ x: parentPoint.x, y: parentPoint.y }, { x: parentPoint.x, y: horizontalLineY }, bendPoint],
            type: 'parent-child',
            isDashed: hasNonBioWithAnyChild
          });
        });
      }
    }
  });

  // Then, create parent-child connections for groups with children
  parentGroups.forEach((group) => {
    if (group.children.length === 0) return;

    // Calculate parent bottom center points (parents are above, so connect from bottom)
    const parentBottomPoints = group.parentIds
      .map((parentId) => getCardBottomCenterPoint(parentId, containerRect, options))
      .filter((point): point is LinePoint => point !== null);

    if (parentBottomPoints.length === 0) return;

    // Calculate parent center point (for vertical drop line)
    const parentCenterX = parentBottomPoints.reduce((sum, point) => sum + point.x, 0) / parentBottomPoints.length;
    const parentCenterY = parentBottomPoints[0].y; // All parents are at same Y level

    // Calculate child top center points (children are below, so connect from top)
    const childTopPoints = group.children
      .map((childId) => getCardTopCenterPoint(childId, containerRect, options))
      .filter((point): point is LinePoint => point !== null);

    if (childTopPoints.length === 0) return;

    // Calculate branch line position (horizontal line for children)
    // Position branch line further from the card edges for better visibility
    // Child connector lines will extend downward from branchY to child top
    const branchY =
      Math.min(...childTopPoints.map((point) => point.y)) -
      options.lineMargin -
      (options.connectorLineExtension ?? DEFAULT_CONNECTOR_EXTENSION);
    const minChildX = Math.min(...childTopPoints.map((point) => point.x));
    const maxChildX = Math.max(...childTopPoints.map((point) => point.x));

    // Spouse lines are already created in the first loop above

    // 2. Create vertical line from parent center down to branch line
    // This is a connector line, but we need to check if any parent-child relationship should be dashed
    // We'll use the first parent's relationship with the first child as a proxy
    const connectorExt = options.connectorLineExtension ?? DEFAULT_CONNECTOR_EXTENSION;
    const verticalStartY =
      group.parentIds.length > 1
        ? parentBottomPoints[0].y + options.lineMargin + connectorExt // From horizontal spouse line (which is now further down)
        : parentCenterY + options.lineMargin; // From single parent

    // Determine if this connector line should be dashed based on parent-child relationships
    // Check if any parent has a non-biological relationship with any child
    const hasNonBiologicalRelationship = group.children.some((childId) =>
      group.parentIds.some((parentId) => {
        const rel = relationships.find(
          (r) =>
            (r.fromFamilyMemberId === parentId && r.toFamilyMemberId === childId) ||
            (r.fromFamilyMemberId === childId && r.toFamilyMemberId === parentId)
        );
        return rel && rel.type !== 'biological' && rel.type !== 'spousal';
      })
    );

    // Extend the main vertical line upward from parent connection and ensure it reaches the branch
    const trunkStart = { x: parentCenterX, y: verticalStartY };
    const trunkBranch = { x: parentCenterX, y: branchY };

    if (childTopPoints.length === 1) {
      const childPoint = childTopPoints[0];
      const childId = group.children[0];
      const connectorPoints =
        group.parentIds.length > 1
          ? [
              {
                x: parentCenterX + (childPoint.x >= parentCenterX ? -CORNER_ARM : CORNER_ARM),
                y: verticalStartY
              },
              { x: parentCenterX, y: verticalStartY },
              trunkBranch,
              { x: childPoint.x, y: branchY },
              childPoint
            ]
          : [trunkStart, trunkBranch, { x: childPoint.x, y: branchY }, childPoint];

      lines.push({
        id: `parent-child-connector-${group.parentIds.join('-')}-to-${childId}`,
        fromFamilyMemberId: group.parentIds[0],
        toFamilyMemberId: childId,
        fromPoint: trunkStart,
        toPoint: childPoint,
        points: connectorPoints,
        type: 'parent-child',
        isDashed: hasNonBiologicalRelationship
      });
    } else {
      const childXs = childTopPoints.map((point) => point.x);
      const trunkGapEnd = parentCenterX - CORNER_ARM;
      const trunkGapStart = parentCenterX + CORNER_ARM;
      const branchBendX = minChildX < parentCenterX ? parentCenterX - CORNER_ARM : parentCenterX + CORNER_ARM;

      const trunkPoints =
        group.parentIds.length > 1
          ? [
              {
                x: parentCenterX + (maxChildX >= parentCenterX ? -CORNER_ARM : CORNER_ARM),
                y: verticalStartY
              },
              { x: parentCenterX, y: verticalStartY },
              trunkBranch,
              { x: branchBendX, y: branchY }
            ]
          : [trunkStart, trunkBranch, { x: branchBendX, y: branchY }];

      lines.push({
        id: `parent-vertical-${group.parentIds.join('-')}`,
        fromFamilyMemberId: group.parentIds[0],
        toFamilyMemberId: group.children[0],
        fromPoint: trunkStart,
        toPoint: { x: branchBendX, y: branchY },
        points: trunkPoints,
        type: 'parent-child',
        isDashed: hasNonBiologicalRelationship
      });

      if (minChildX < trunkGapEnd) {
        pushHorizontalSegments(
          lines,
          `branch-horizontal-left-${group.parentIds.join('-')}-to-${group.children.join('-')}`,
          minChildX,
          trunkGapEnd,
          branchY,
          childXs,
          group.children[0],
          group.children[1] ?? group.children[0]
        );
      }

      if (trunkGapStart < maxChildX) {
        pushHorizontalSegments(
          lines,
          `branch-horizontal-right-${group.parentIds.join('-')}-to-${group.children.join('-')}`,
          trunkGapStart,
          maxChildX,
          branchY,
          childXs,
          group.children[0],
          group.children[1] ?? group.children[0]
        );
      }

      childTopPoints.forEach((childPoint, index) => {
        const childId = group.children[index];
        const hasChildNonBiologicalRelationship = group.parentIds.some((parentId) => {
          const rel = relationships.find(
            (r) =>
              (r.fromFamilyMemberId === parentId && r.toFamilyMemberId === childId) ||
              (r.fromFamilyMemberId === childId && r.toFamilyMemberId === parentId)
          );
          return rel && rel.type !== 'biological' && rel.type !== 'spousal';
        });
        const approachX = childPoint.x < parentCenterX ? childPoint.x + CORNER_ARM : childPoint.x - CORNER_ARM;

        lines.push({
          id: `child-vertical-${group.parentIds.join('-')}-${childId}`,
          fromFamilyMemberId: group.parentIds[0],
          toFamilyMemberId: childId,
          fromPoint: { x: childPoint.x, y: branchY },
          toPoint: childPoint,
          points: [{ x: approachX, y: branchY }, { x: childPoint.x, y: branchY }, childPoint],
          type: 'parent-child',
          isDashed: hasChildNonBiologicalRelationship
        });
      });
    }
  });

  return lines;
}

/**
 * Get SVG path string for a straight line between two points
 */
export function getStraightLinePath(fromPoint: LinePoint, toPoint: LinePoint): string {
  return `M ${fromPoint.x} ${fromPoint.y} L ${toPoint.x} ${toPoint.y}`;
}

/**
 * Build an SVG path through polyline vertices with rounded corners at each bend.
 */
export function getPolylinePathWithRoundedCorners(
  points: LinePoint[],
  cornerRadius: number = STEPPED_LINE_CORNER_RADIUS
): string {
  if (points.length < 2) {
    return '';
  }
  if (points.length === 2) {
    return getStraightLinePath(points[0], points[1]);
  }

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    const inDx = curr.x - prev.x;
    const inDy = curr.y - prev.y;
    const outDx = next.x - curr.x;
    const outDy = next.y - curr.y;
    const inLen = Math.hypot(inDx, inDy);
    const outLen = Math.hypot(outDx, outDy);

    if (inLen < 1e-6 || outLen < 1e-6) {
      path += ` L ${curr.x} ${curr.y}`;
      continue;
    }

    const radius = Math.min(cornerRadius, inLen / 2, outLen / 2);
    if (radius < 1) {
      path += ` L ${curr.x} ${curr.y}`;
      continue;
    }

    const inUx = inDx / inLen;
    const inUy = inDy / inLen;
    const outUx = outDx / outLen;
    const outUy = outDy / outLen;

    const startCorner = { x: curr.x - inUx * radius, y: curr.y - inUy * radius };
    const endCorner = { x: curr.x + outUx * radius, y: curr.y + outUy * radius };
    const cross = inUx * outUy - inUy * outUx;
    // SVG Y-axis points down, so sweep is the inverse of the standard cross-product handedness.
    const sweep = cross > 0 ? 1 : 0;

    path += ` L ${startCorner.x} ${startCorner.y}`;
    path += ` A ${radius} ${radius} 0 0 ${sweep} ${endCorner.x} ${endCorner.y}`;
  }

  const last = points[points.length - 1];
  path += ` L ${last.x} ${last.y}`;
  return path;
}

/**
 * @deprecated Connectors are drawn as polylines; use getPolylinePathWithRoundedCorners.
 */
export function getSteppedLinePath(
  fromPoint: LinePoint,
  toPoint: LinePoint,
  _options: LineCalculationOptions = DEFAULT_OPTIONS,
  cornerRadius: number = STEPPED_LINE_CORNER_RADIUS
): string {
  if (Math.abs(fromPoint.x - toPoint.x) < 5) {
    return getStraightLinePath(fromPoint, toPoint);
  }

  if (Math.abs(fromPoint.y - toPoint.y) < 5) {
    return getStraightLinePath(fromPoint, toPoint);
  }

  const midY = fromPoint.y + (toPoint.y - fromPoint.y) / 2;
  return getPolylinePathWithRoundedCorners(
    [fromPoint, { x: fromPoint.x, y: midY }, { x: toPoint.x, y: midY }, toPoint],
    cornerRadius
  );
}
